/** @jsxImportSource theme-ui */
import { Box, Flex, Grid, Heading, Label, Select, Spinner, Text } from 'theme-ui';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import qs from 'qs';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import Button from '@/components/Button';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import DataTable from '@/components/DataTable';
import { Container } from '@/components/styled';
import {
    useCancelVossLicenseAuditRunMutation,
    useGetVossLicenseAuditFindingsQuery,
    useGetVossLicenseAuditRunsQuery,
    useStartVossLicenseAuditPreviewMutation,
} from '@/store/apiService';
import {
    GetVossLicenseAuditFindingsQuery,
    VossLicenseAuditDecisionAction,
    VossLicenseAuditFindingItem,
    VossLicenseAuditFindingKind,
    VossLicenseAuditRunItem,
    VossLicenseAuditRunMode,
    VossLicenseAuditRunStatus,
} from '@/types/types';
import {
    decisionDescriptions,
    decisionLabels,
    kindDescriptions,
    kindLabels,
} from '@/containers/voss/SubscriptionAuditFilters';

const PAGE_SIZE = 50;
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';
const POLLING_INTERVAL_MS = 5000;
const RUNS_TAKE = 20;

const formatDate = (value?: string | null) =>
    value ? format(new Date(value), 'd MMM yyyy, HH:mm') : '—';

const modeColors: Record<VossLicenseAuditRunMode, string> = {
    Preview: '#1565c0',
    Apply: '#6a1b9a',
};

const statusColors: Record<VossLicenseAuditRunStatus, string> = {
    Running: '#1565c0',
    Completed: '#2e7d32',
    Aborted: '#ef6c00',
    Failed: '#c62828',
    Stale: '#757575',
};

const decisionColors: Record<VossLicenseAuditDecisionAction, string> = {
    Applied: '#2e7d32',
    WouldApply: '#1565c0',
    SkippedLocalChangePending: '#f9a825',
    SkippedPendingScheduledChange: '#f9a825',
    SkippedCap: '#ef6c00',
    ReportOnly: '#757575',
};

const Badge = ({ label, color, title }: { label: string; color?: string; title?: string }) => (
    <Text
        title={title}
        sx={{
            display: 'inline-block',
            px: 2,
            py: '2px',
            borderRadius: 'sm',
            fontSize: 0,
            fontWeight: 'bold',
            color: 'white',
            whiteSpace: 'nowrap',
            backgroundColor: color ?? 'gray.5',
        }}
    >
        {label}
    </Text>
);

const StatCard = ({
    label,
    value,
    hint,
}: {
    label: string;
    value: React.ReactNode;
    hint?: string;
}) => (
    <Box sx={{ border: '1px solid', borderColor: 'gray.3', borderRadius: 'sm', p: 2 }}>
        <Text sx={{ display: 'block', fontSize: 0, color: 'gray.6' }}>{label}</Text>
        <Text sx={{ display: 'block', fontSize: 4, fontVariantNumeric: 'tabular-nums' }}>
            {value}
        </Text>
        {hint ? <Text sx={{ display: 'block', fontSize: 0, color: 'gray.6' }}>{hint}</Text> : null}
    </Box>
);

const Progress = ({ checked, total }: { checked: number; total: number }) => {
    const width = total > 0 ? Math.min(100, Math.round((checked / total) * 100)) : 0;
    return (
        <Box sx={{ minWidth: '200px' }}>
            <Box sx={{ height: '6px', backgroundColor: 'gray.2', borderRadius: 'sm', mb: '2px' }}>
                <Box
                    sx={{
                        width: `${width}%`,
                        height: '100%',
                        backgroundColor: 'primary',
                        borderRadius: 'sm',
                    }}
                />
            </Box>
            <Text sx={{ fontSize: 0, color: 'gray.6', fontVariantNumeric: 'tabular-nums' }}>
                {`${checked} / ${total} companies checked`}
            </Text>
        </Box>
    );
};

const runLabel = (run: VossLicenseAuditRunItem) =>
    `#${run.Id} ${run.Mode} · ${run.Status} · ${formatDate(run.StartedDate)}`;

const toQuery = (params: qs.ParsedQs, skip: number): GetVossLicenseAuditFindingsQuery => {
    const value = (key: string) => (typeof params[key] === 'string' ? (params[key] as string) : '');
    const query: GetVossLicenseAuditFindingsQuery = { Skip: skip, Take: PAGE_SIZE };
    const runId = Number(value('RunId'));
    if (value('RunId') && Number.isFinite(runId)) query.RunId = runId;
    if (value('Kind')) query.Kind = value('Kind') as GetVossLicenseAuditFindingsQuery['Kind'];
    if (value('Decision'))
        query.Decision = value('Decision') as GetVossLicenseAuditFindingsQuery['Decision'];
    else if (value('ShowReportOnly') !== 'true') query.ExcludeReportOnly = true;
    if (value('CompanyId')) query.CompanyId = value('CompanyId');
    return query;
};

const pageFromParams = (params: qs.ParsedQs) => {
    const page = Number(params.page);
    return Number.isInteger(page) && page > 1 ? page : 1;
};

const findingColumns = [
    {
        columnKey: 'CompanyName',
        Header: 'Company',
        accessor: 'CompanyName',
        Cell: ({ row }: any) => {
            const finding: VossLicenseAuditFindingItem = row.original;
            if (finding.CompanyId === EMPTY_GUID) {
                return <Text sx={{ color: 'gray.6' }}>—</Text>;
            }
            return (
                <Box>
                    <Link to={`/clients/${finding.CompanyId}`}>
                        {finding.CompanyName ?? finding.CompanyId}
                    </Link>
                    <Text sx={{ display: 'block', fontSize: 0, color: 'gray.6' }}>
                        {finding.CompanyId}
                    </Text>
                </Box>
            );
        },
    },
    {
        columnKey: 'Kind',
        Header: 'Kind',
        accessor: 'Kind',
        Cell: ({ cell }: any) => (
            <Text title={kindDescriptions[cell.value as VossLicenseAuditFindingKind]}>
                {kindLabels[cell.value as VossLicenseAuditFindingKind] ?? cell.value}
            </Text>
        ),
    },
    {
        columnKey: 'Decision',
        Header: 'Decision',
        accessor: 'Decision',
        Cell: ({ cell }: any) => (
            <Badge
                label={decisionLabels[cell.value as VossLicenseAuditDecisionAction] ?? cell.value}
                color={decisionColors[cell.value as VossLicenseAuditDecisionAction]}
                title={decisionDescriptions[cell.value as VossLicenseAuditDecisionAction]}
            />
        ),
    },
    {
        columnKey: 'LicenseTypeName',
        Header: 'Local license',
        accessor: (row: VossLicenseAuditFindingItem) =>
            row.LicenseTypeName ?? row.LicenseTypeId ?? '—',
    },
    {
        columnKey: 'VossTierName',
        Header: 'Voss tier',
        accessor: (row: VossLicenseAuditFindingItem) =>
            row.VossTierName ?? row.VossTierExternalId ?? '—',
    },
    {
        columnKey: 'AddOnLicenseTypeName',
        Header: 'Add-on',
        accessor: (row: VossLicenseAuditFindingItem) =>
            row.AddOnLicenseTypeName ?? row.AddOnLicenseTypeId ?? '',
        Cell: ({ row }: any) => {
            const finding: VossLicenseAuditFindingItem = row.original;
            const name = finding.AddOnLicenseTypeName ?? finding.AddOnLicenseTypeId;
            const hasQuantities = finding.LocalQuantity != null || finding.VossQuantity != null;
            if (name == null && !hasQuantities) return <Text sx={{ color: 'gray.6' }}>—</Text>;
            return (
                <Box>
                    {name != null ? <Text>{name}</Text> : null}
                    {hasQuantities ? (
                        <Text
                            sx={{
                                display: 'block',
                                fontSize: 0,
                                color: 'gray.6',
                                fontVariantNumeric: 'tabular-nums',
                            }}
                        >
                            qty {finding.LocalQuantity ?? '—'}→{finding.VossQuantity ?? '—'}
                        </Text>
                    ) : null}
                </Box>
            );
        },
    },
    {
        columnKey: 'VossSubscriptionId',
        Header: 'Subscription id',
        accessor: (row: VossLicenseAuditFindingItem) => row.VossSubscriptionId ?? '—',
    },
    { columnKey: 'Note', Header: 'Note', accessor: 'Note' },
];

const SubscriptionAudit = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const search = searchParams.toString();
    const params = useMemo(() => qs.parse(search), [search]);
    const page = pageFromParams(params);
    const skip = (page - 1) * PAGE_SIZE;
    const [polling, setPolling] = useState(false);
    const pollingInterval = polling ? POLLING_INTERVAL_MS : 0;

    const runsState = useGetVossLicenseAuditRunsQuery({ Take: RUNS_TAKE }, { pollingInterval });
    const findingsState = useGetVossLicenseAuditFindingsQuery(toQuery(params, skip), {
        pollingInterval,
    });
    const [startPreview, startPreviewState] = useStartVossLicenseAuditPreviewMutation();
    const [cancelRun, cancelRunState] = useCancelVossLicenseAuditRunMutation();
    const [confirmCancel, setConfirmCancel] = useState(false);

    const runs = runsState.data?.Runs ?? [];
    const data = findingsState.data;
    const run = data?.Run;
    const summary = run?.Summary;
    const total = data?.Total ?? 0;
    const error: any = findingsState.error;
    const isRunning = run?.Status === 'Running';
    const newestPreview = runs.find((item) => item.Mode === 'Preview');
    const previewRunning =
        (isRunning && run?.Mode === 'Preview') || newestPreview?.Status === 'Running';
    const selectedRunId = typeof params.RunId === 'string' ? params.RunId : '';

    useEffect(() => {
        setPolling(isRunning);
    }, [isRunning]);

    const updateParams = (changes: Record<string, string | undefined>) => {
        const next: Record<string, unknown> = { ...params, ...changes };
        Object.keys(next).forEach((key) => {
            if (next[key] === undefined || next[key] === '') delete next[key];
        });
        setSearchParams(qs.stringify(next));
    };

    const setPage = (next: number) => updateParams({ page: next > 1 ? String(next) : undefined });

    const selectRun = (runId: string) =>
        updateParams({ RunId: runId || undefined, page: undefined });

    const checkNow = () =>
        startPreview({})
            .unwrap()
            .then((res) => {
                if (res.AlreadyRunning) {
                    toast.info('A preview is already running');
                } else {
                    toast.success('Preview started');
                }
                selectRun(res.RunId ? String(res.RunId) : '');
            })
            .catch((err) =>
                toast.error(
                    err?.data?.ResponseStatus?.Message ??
                        err?.message ??
                        'Could not start a preview',
                    { autoClose: 5000 }
                )
            );

    const cancelCurrentRun = () => {
        if (!run) return;
        cancelRun({ Id: run.Id })
            .unwrap()
            .then((res) => {
                setConfirmCancel(false);
                if (res.Cancelled) {
                    toast.success(`Run #${run.Id} cancelled; it stops after its current batch`);
                } else {
                    toast.info(`Run #${run.Id} had already finished`);
                }
            })
            .catch((err) => {
                setConfirmCancel(false);
                toast.error(
                    err?.data?.ResponseStatus?.Message ??
                        err?.message ??
                        'Could not cancel the run',
                    { autoClose: 5000 }
                );
            });
    };

    if (findingsState.isLoading) {
        return <Spinner size={16} />;
    }

    return (
        <Flex sx={{ flexDirection: 'column', gap: 3 }}>
            <Flex sx={{ gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <Box sx={{ minWidth: '320px' }}>
                    <Label>Run</Label>
                    <Select
                        value={selectedRunId}
                        backgroundColor="gray.1"
                        onChange={(e) => selectRun(e.target.value)}
                    >
                        <option value="">Newest run</option>
                        {runs.map((item) => (
                            <option key={item.Id} value={String(item.Id)}>
                                {runLabel(item)}
                            </option>
                        ))}
                    </Select>
                </Box>
                <Button
                    type="button"
                    isLoading={startPreviewState.isLoading}
                    disabled={previewRunning || startPreviewState.isLoading}
                    title={previewRunning ? 'A preview is already running' : undefined}
                    onClick={checkNow}
                >
                    Check now
                </Button>
                {findingsState.isFetching ? <Spinner size={16} /> : null}
            </Flex>

            {confirmCancel && run ? (
                <ConfirmationModal
                    hideOnClickOutside
                    loading={cancelRunState.isLoading}
                    headingText={`Cancel run #${run.Id}`}
                    content={`The ${run.Mode} run stops after its current batch of companies and is marked Aborted. Findings saved so far are kept. Continue?`}
                    onNo={() => setConfirmCancel(false)}
                    onYes={cancelCurrentRun}
                />
            ) : null}

            {error ? (
                <Box
                    sx={{
                        border: '1px solid',
                        borderColor: statusColors.Failed,
                        borderRadius: 'sm',
                        p: 2,
                    }}
                >
                    <Text sx={{ color: statusColors.Failed }}>
                        {error?.data?.ResponseStatus?.Message ??
                            error?.error ??
                            'The audit request failed.'}
                    </Text>
                </Box>
            ) : null}

            {run ? (
                <>
                    <Box
                        sx={{
                            border: '1px solid',
                            borderColor: 'gray.3',
                            borderRadius: 'sm',
                            p: 2,
                        }}
                    >
                        <Flex sx={{ gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Text sx={{ fontWeight: 'bold' }}>Run #{run.Id}</Text>
                            <Badge label={run.Mode} color={modeColors[run.Mode]} />
                            <Badge label={run.Status} color={statusColors[run.Status]} />
                            <Text sx={{ fontSize: 0, color: 'gray.6' }}>
                                Started {formatDate(run.StartedDate)}
                            </Text>
                            <Text sx={{ fontSize: 0, color: 'gray.6' }}>
                                Finished {formatDate(run.FinishedDate)}
                            </Text>
                            {run.TriggeredBy ? (
                                <Text sx={{ fontSize: 0, color: 'gray.6' }}>
                                    by {run.TriggeredBy}
                                </Text>
                            ) : null}
                            <Progress checked={run.CompaniesChecked} total={run.CompaniesTotal} />
                            {isRunning ? (
                                <Button
                                    variant="danger"
                                    type="button"
                                    isLoading={cancelRunState.isLoading}
                                    disabled={cancelRunState.isLoading}
                                    onClick={() => setConfirmCancel(true)}
                                >
                                    Cancel run
                                </Button>
                            ) : null}
                        </Flex>
                        {run.Summary?.FailureReason ? (
                            <Text
                                sx={{
                                    display: 'block',
                                    mt: 2,
                                    fontSize: 1,
                                    color: statusColors[run.Status],
                                    wordBreak: 'break-word',
                                }}
                            >
                                {run.Status === 'Aborted' ? 'Stopped' : 'Failed'}:{' '}
                                {run.Summary.FailureReason}
                            </Text>
                        ) : null}
                    </Box>

                    <Grid
                        sx={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 2 }}
                    >
                        <StatCard
                            label={run.Mode === 'Preview' ? 'Changes tonight' : 'Changes applied'}
                            value={summary?.MutationsApplied ?? '—'}
                            hint={
                                summary
                                    ? `${summary.MutationsSkippedByCap} more over the cap`
                                    : undefined
                            }
                        />
                        <StatCard label="Tier mismatches" value={summary?.TierMismatches ?? '—'} />
                        <StatCard
                            label="Terminations"
                            value={
                                summary
                                    ? summary.SubscriptionsTerminated + summary.AddOnsTerminated
                                    : '—'
                            }
                            hint={
                                summary
                                    ? `${summary.SubscriptionsTerminated} subscriptions · ${summary.AddOnsTerminated} add-ons`
                                    : undefined
                            }
                        />
                        <StatCard
                            label="Relinked / created"
                            value={
                                summary
                                    ? (summary.SubscriptionsRelinked ?? 0) +
                                      (summary.AddOnsCreated ?? 0)
                                    : '—'
                            }
                            hint={
                                summary
                                    ? `${
                                          summary.SubscriptionsRelinked ?? 0
                                      } subscriptions relinked · ${
                                          summary.AddOnsCreated ?? 0
                                      } add-ons created`
                                    : undefined
                            }
                        />
                        <StatCard
                            label="End dates / plans / duplicates"
                            value={
                                summary
                                    ? (summary.ValidToSynced ?? 0) +
                                      (summary.PlansSynced ?? 0) +
                                      (summary.DuplicatesEnded ?? 0)
                                    : '—'
                            }
                            hint={
                                summary
                                    ? `${summary.ValidToSynced ?? 0} end dates · ${
                                          summary.PlansSynced ?? 0
                                      } plans · ${summary.DuplicatesEnded ?? 0} duplicates ended`
                                    : undefined
                            }
                        />
                        <StatCard
                            label="Billing info updates"
                            value={
                                summary && run.Mode === 'Preview'
                                    ? summary.BillingUpdates ?? 0
                                    : '—'
                            }
                            hint={
                                summary && run.Mode === 'Preview'
                                    ? `${
                                          summary.ChangeLogRowsPreviewed ?? 0
                                      } change-log rows previewed · ${
                                          summary.ChangeLogRowsUnresolved ?? 0
                                      } unresolved`
                                    : 'The change-log job runs separately before an Apply run'
                            }
                        />
                        <StatCard
                            label="Needs a person"
                            value={
                                summary
                                    ? summary.AddOnMismatches +
                                      (summary.SubscriptionsAmbiguous ?? 0)
                                    : '—'
                            }
                            hint={
                                summary
                                    ? `${summary.AddOnMismatches} add-on issues · ${
                                          summary.SubscriptionsAmbiguous ?? 0
                                      } ambiguous subscriptions`
                                    : undefined
                            }
                        />
                        <StatCard
                            label="Skipped – local change pending"
                            value={summary?.SkippedLocalChangePending ?? '—'}
                        />
                        <StatCard
                            label="Skipped – cap"
                            value={summary?.MutationsSkippedByCap ?? '—'}
                            hint={`max ${run.MaxMutations} mutations`}
                        />
                        <StatCard label="Failed" value={summary?.FailedToCheck ?? '—'} />
                    </Grid>
                </>
            ) : !error && !findingsState.isFetching ? (
                <Text sx={{ color: 'gray.6' }}>No audit runs yet.</Text>
            ) : null}

            <Heading as="h3">Findings</Heading>
            <Box sx={{ border: '1px solid', borderColor: 'gray.3', borderRadius: 'sm', p: 2 }}>
                <Text sx={{ display: 'block', fontWeight: 'bold', mb: 1 }}>
                    What the decisions mean
                </Text>
                <Grid sx={{ gridTemplateColumns: 'auto 1fr', gap: 2, alignItems: 'start' }}>
                    {(Object.keys(decisionLabels) as VossLicenseAuditDecisionAction[]).map(
                        (decision) => (
                            <Fragment key={decision}>
                                <Badge
                                    label={decisionLabels[decision]}
                                    color={decisionColors[decision]}
                                />
                                <Text sx={{ fontSize: 1 }}>{decisionDescriptions[decision]}</Text>
                            </Fragment>
                        )
                    )}
                </Grid>
            </Box>
            <Box
                as="details"
                sx={{ border: '1px solid', borderColor: 'gray.3', borderRadius: 'sm', p: 2 }}
            >
                <Text as="summary" sx={{ fontWeight: 'bold', cursor: 'pointer' }}>
                    What the kinds mean
                </Text>
                <Grid sx={{ gridTemplateColumns: 'auto 1fr', gap: 2, alignItems: 'start', mt: 2 }}>
                    {(Object.keys(kindLabels) as VossLicenseAuditFindingKind[]).map((kind) => (
                        <Fragment key={kind}>
                            <Text sx={{ fontSize: 1, fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                                {kindLabels[kind]}
                            </Text>
                            <Text sx={{ fontSize: 1 }}>{kindDescriptions[kind]}</Text>
                        </Fragment>
                    ))}
                </Grid>
            </Box>
            <Container>
                <DataTable
                    data={data?.Items ?? []}
                    columns={findingColumns}
                    isFetching={findingsState.isFetching}
                />
            </Container>
            <Flex sx={{ alignItems: 'center', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                    variant="secondary"
                    type="button"
                    disabled={page <= 1 || findingsState.isFetching}
                    onClick={() => setPage(page - 1)}
                >
                    Previous
                </Button>
                <Button
                    variant="secondary"
                    type="button"
                    disabled={skip + PAGE_SIZE >= total || findingsState.isFetching}
                    onClick={() => setPage(page + 1)}
                >
                    Next
                </Button>
                <Text sx={{ color: 'gray.6', fontSize: 0 }}>
                    {total === 0
                        ? 'No findings'
                        : `${skip + 1}–${Math.min(skip + PAGE_SIZE, total)} of ${total}`}
                </Text>
            </Flex>
        </Flex>
    );
};

export default SubscriptionAudit;
